import { useCallback, useState } from 'react';
import {
  addMonths,
  eachMonthOfInterval,
  format,
  isSameMonth,
  startOfMonth,
  startOfYear,
} from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';
import { useSupabase } from '../contexts/SupabaseContext';
import { useBusiness } from '../contexts/BusinessContext';

export type NewLeadsByMonthRow = {
  monthKey: string;
  label: string;
  count: number;
  isCurrentMonth: boolean;
};

export type NewLeadsSourceRow = {
  source: string;
  count: number;
};

function monthKeyInTimezone(iso: string, timezone: string) {
  return format(toZonedTime(new Date(iso), timezone), 'yyyy-MM');
}

/** New leads by created_at per month, January through current month (business timezone). */
export function useNewLeadsByMonthYtdBreakdown() {
  const supabase = useSupabase();
  const { selectedBusiness } = useBusiness();
  const businessId = selectedBusiness?.id;
  const timezone =
    selectedBusiness?.timezone ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'UTC';

  const [months, setMonths] = useState<NewLeadsByMonthRow[]>([]);
  const [sourcesByMonth, setSourcesByMonth] = useState<Record<string, NewLeadsSourceRow[]>>({});
  const [yearTotal, setYearTotal] = useState(0);
  const [yearLabel, setYearLabel] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase || !businessId) {
      setMonths([]);
      setSourcesByMonth({});
      setYearTotal(0);
      setYearLabel('');
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const zonedNow = toZonedTime(new Date(), timezone);
      const yearStartZoned = startOfYear(zonedNow);
      const currentMonthZoned = startOfMonth(zonedNow);

      setYearLabel(format(zonedNow, 'yyyy'));

      const rangeStart = fromZonedTime(yearStartZoned, timezone).toISOString();
      const rangeEnd = fromZonedTime(
        startOfMonth(addMonths(zonedNow, 1)),
        timezone
      ).toISOString();

      const { data, error: queryError } = await supabase
        .from('pipeline_opportunities')
        .select('created_at, source')
        .eq('business_id', businessId)
        .not('created_at', 'is', null)
        .gte('created_at', rangeStart)
        .lt('created_at', rangeEnd);

      if (queryError) throw queryError;

      const countByMonth = new Map<string, number>();
      const sourceMapByMonth = new Map<string, Map<string, number>>();
      for (const opportunity of data ?? []) {
        if (!opportunity.created_at) continue;
        const key = monthKeyInTimezone(opportunity.created_at, timezone);
        countByMonth.set(key, (countByMonth.get(key) ?? 0) + 1);

        const rawSource = (opportunity.source as string | null)?.trim();
        const source = rawSource && rawSource.length > 0 ? rawSource : 'Unknown';
        let monthSources = sourceMapByMonth.get(key);
        if (!monthSources) {
          monthSources = new Map<string, number>();
          sourceMapByMonth.set(key, monthSources);
        }
        monthSources.set(source, (monthSources.get(source) ?? 0) + 1);
      }

      const sourcesRecord: Record<string, NewLeadsSourceRow[]> = {};
      for (const [key, sourceMap] of sourceMapByMonth.entries()) {
        sourcesRecord[key] = Array.from(sourceMap.entries())
          .map(([source, count]) => ({ source, count }))
          .filter((row) => row.count > 0)
          .sort((a, b) => b.count - a.count);
      }

      const monthDates = eachMonthOfInterval({
        start: yearStartZoned,
        end: currentMonthZoned,
      });

      const rows: NewLeadsByMonthRow[] = monthDates.map((month) => {
        const key = format(month, 'yyyy-MM');
        return {
          monthKey: key,
          label: format(month, 'MMMM yyyy'),
          count: countByMonth.get(key) ?? 0,
          isCurrentMonth: isSameMonth(month, zonedNow),
        };
      });

      const total = rows.reduce((sum, row) => sum + row.count, 0);
      setMonths(rows);
      setSourcesByMonth(sourcesRecord);
      setYearTotal(total);
    } catch (err) {
      console.error('Error loading new leads by month YTD breakdown:', err);
      setMonths([]);
      setSourcesByMonth({});
      setYearTotal(0);
      setError('Could not load leads breakdown.');
    } finally {
      setLoading(false);
    }
  }, [supabase, businessId, timezone]);

  return { months, sourcesByMonth, yearTotal, yearLabel, loading, error, load };
}
